import { useEffect, useState } from 'react';
import { useI18n } from '../../lib/i18n.js';
import {
  useNotificationPushPublicKey,
  useNotificationPushSubscribe,
  useNotificationPushUnsubscribe,
} from '../../queries/index.js';
import { ensurePushServiceWorker } from './push-sw.js';

/**
 * The browser Web-Push opt-in (clarity-smart-notifications Req 5). Reads the
 * VAPID public key; when present, offers an enable button that requests
 * permission, registers the push service worker, subscribes the browser, and
 * POSTs the subscription. Degrades gracefully: unsupported / blocked / push-off
 * states show a plain message rather than a dead control.
 */

function urlBase64ToArrayBuffer(base64: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) view[i] = raw.charCodeAt(i);
  return buffer;
}

export function PushToggle() {
  const { t } = useI18n();
  const keyQuery = useNotificationPushPublicKey();
  const subscribe = useNotificationPushSubscribe();
  const unsubscribe = useNotificationPushUnsubscribe();
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supported =
    typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
  const publicKey = keyQuery.data?.status === 200 ? keyQuery.data.body.publicKey : null;

  useEffect(() => {
    if (!supported) return;
    void navigator.serviceWorker
      .getRegistration('/app/')
      .then((reg) => reg?.pushManager.getSubscription())
      .then((sub) => setEnabled(Boolean(sub)))
      .catch(() => {});
  }, [supported]);

  if (!supported) return <p className="muted">{t('notif.push.unsupported')}</p>;
  if (publicKey === null) {
    // Push not configured on the server — still show the always-on in-app line.
    return <p className="muted">{t('notif.chan.inapp')} · {t('notif.push.enabled')}</p>;
  }

  const enable = async (): Promise<void> => {
    setError(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setError(t('notif.push.blocked'));
        return;
      }
      const reg = await ensurePushServiceWorker();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToArrayBuffer(publicKey),
      });
      const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;
      subscribe.mutate({
        body: { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } },
      });
      setEnabled(true);
    } catch {
      setError(t('notif.push.blocked'));
    }
  };

  const disable = async (): Promise<void> => {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/app/');
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        unsubscribe.mutate({ body: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setEnabled(false);
    } catch {
      /* best-effort */
    }
  };

  return (
    <div className="notif-pref-row">
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        className="notif-switch"
        onClick={() => void (enabled ? disable() : enable())}
        aria-label={t('notif.push.enable')}
      />
      <div className="notif-pref-row__main">
        <b>{enabled ? t('notif.push.enabled') : t('notif.push.enable')}</b>
        {error && <p className="notif-pref-row__err">{error}</p>}
      </div>
    </div>
  );
}
